import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  persistReducer,
  persistStore,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import { productsApi } from '@/app/modules/products/products.services';
import { profileApi } from '@/app/modules/profile/profile.services';
import { stipsApi } from '@/app/modules/stips/stips.services';
import { referencesApi } from '@/app/modules/uploadStips/components/References/references.services';
import { documentsApi } from '@/app/modules/uploadStips/uploadStips.services';
import { appApi } from '@/app.services';
import authReducer from '@/store/reducers/authSlice';
import uploadReducer from '@/store/reducers/uploadSlice';
import { decryptAndValidateApi } from './api/decryptAndValidateApi';
import { eventsApi } from './api/eventsApi';
import { gridsSlice } from './reducers/gridsSlice';
import uiReducer from './reducers/uiSlice';

const persistConfig = {
  key: 'auth',
  storage,
  whitelist: ['email', 'metadataProcessed', 'contactId', 'userProfile', 'token' ], // Persist specific fields
};

const persistedAuthReducer = persistReducer(persistConfig, authReducer);

const uploadPersistConfig = {
  key: 'upload',
  storage,
  whitelist: ['fileMd5Map', 'filesOcrDetails', 'failedFileNames'],
};

const persistedUploadReducer = persistReducer(uploadPersistConfig, uploadReducer);

export const store = configureStore({
  reducer: {
    auth: persistedAuthReducer,
    ui: uiReducer,
    upload: persistedUploadReducer,
    grids: gridsSlice.reducer,
    [referencesApi.reducerPath]: referencesApi.reducer,
    [documentsApi.reducerPath]: documentsApi.reducer,
    [eventsApi.reducerPath]: eventsApi.reducer,
    [profileApi.reducerPath]: profileApi.reducer,
    [appApi.reducerPath]: appApi.reducer,
    [stipsApi.reducerPath]: stipsApi.reducer,
    [decryptAndValidateApi.reducerPath]: decryptAndValidateApi.reducer,
    [productsApi.reducerPath]: productsApi.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(
      documentsApi.middleware, 
      referencesApi.middleware, 
      eventsApi.middleware, 
      profileApi.middleware, 
      appApi.middleware, 
      stipsApi.middleware, 
      decryptAndValidateApi.middleware,
      productsApi.middleware
    ),
  devTools: import.meta.env.DEV,
});

setupListeners(store.dispatch);

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
